import { Button, Checkbox, Select } from "antd";
import { useState, useRef, useEffect, useMemo } from "react";

import ArgsEditor from "./TrainingArgsEditor";
import { ArgumentHistory } from "./TrainingArgumentHistory";
import { TrainingChart } from "./TrainingRun";

import { getTrainingChart, getArgumentChanges } from "../api";


export const MA_COUNT_MAP = {
    0: 100,
    100: 10,
    500: 50,
    1000: 50,
    5000: 50,
    10000: 100,
};

export const RunConfig = (props) => {
    const { runId, statusRef } = props;

    const [active, setActive] = useState('args');
    const [chart, setChart] = useState([]);
    // const [maCount, setMaCount] = useState(64);
    const [autorefresh, setAutorefresh] = useState(true);
    const [showChart, setShowChart] = useState(true);
    const [showMa, setShowMa] = useState(true);
    const [showMarkers, setShowMarkers] = useState(true);
    const [hideOOM, setHideOOM] = useState(true);
    const [useEma, setUseEma] = useState(true);
    const [refreshObj, setRefreshObj] = useState([]);
    const [changes, setChanges] = useState([]);
    const [range, setRange] = useState([0, -1]);
    const [lastN, setLastN] = useState(10000);
    const refreshing = useRef(false);

    const maCount = MA_COUNT_MAP[lastN];

    useEffect(() => {
        if (refreshing.current) {
            return;
        }

        refreshing.current = true;

        const startStep = statusRef?.current?.current_status?.step ?? 0;

        getTrainingChart(runId, 10000, Math.max(0, lastN ? (startStep - lastN) : 0)).then(data => {
            const result = [];

            for (let step in data) {
                if (hideOOM && data[step].loss === 1.0) {
                    continue;
                }

                result.push({ x: Number(step), y: data[step].loss });
            }

            return result;
        }).then(setChart)
            .finally(() => refreshing.current = false);
    }, [runId, refreshObj, statusRef, lastN, hideOOM]);

    useEffect(() => {
        let key = [0];

        function refresh() {
            if (autorefresh) setRefreshObj([]); // New object triggers re-render
            key[0] = window.setTimeout(() => refresh(), 1000);
        }

        refresh();
        return () => window.clearTimeout(key[0]);
    }, [autorefresh]);

    const ma50 = useMemo(() => {
        if (!maCount || !chart || !chart.length) {
            return [];
        }

        const result = [];

        if (useEma) {
            const alpha = 2 / (maCount + 1);
            let ema = chart[0].y;

            for (let i = 0; i < chart.length; i++) {
                ema = alpha * chart[i].y + (1 - alpha) * ema;
                result.push({ x: chart[i].x, y: ema });
            }
        } else {
            for (let i = 0; i <= chart.length - maCount; i++) {
                let avg = 0;
                for (let n = 0; n < maCount; n++) {
                    avg += chart[i + n].y / maCount;
                }

                result.push({ x: chart[i + maCount - 1].x, y: avg });
            }
        }

        return result;
    }, [chart, maCount, useEma]);

    const chartData = useMemo(() => {
        const charts = [];

        if (showChart) {
            charts.push({
                "id": "loss",
                "color": "red",
                "data": chart,
            });
        }

        if (showMa) {
            charts.push({
                "id": "MA50",
                "color": "blue",
                "data": ma50
            });
        }

        return charts;
    }, [chart, ma50, showChart, showMa]);


    useEffect(() => {
        getArgumentChanges(runId).then(changes => {
            const remapped = changes;
            setChanges(remapped);
        });
    }, [runId]);

    const lossClip = statusRef?.current?.current_status?.args?.loss_clip ?? 0;

    return (
        <div>
            <div className="run-config-tabs">
                <Button className="tab-btn" disabled={active === 'args'} onClick={() => setActive('args')}>Args</Button>
                <Button className="tab-btn" disabled={active === 'history'} onClick={() => setActive('history')}>History</Button>
                <Button className="tab-btn" disabled={active === 'graph'} onClick={() => setActive('graph')}>Loss graph</Button>
            </div>

            <div className="run-config-tab" style={{ display: active === 'args' ? undefined : 'none' }}>
                <ArgsEditor runId={runId} statusRef={statusRef} />
            </div>

            <div className="run-config-tab" style={{ display: active === 'history' ? undefined : 'none' }}>
                <ArgumentHistory changes={changes} runId={runId} />
            </div>

            <div className="run-config-tab" style={{ display: active === 'graph' ? undefined : 'none' }}>
                <div>
                    <Checkbox checked={autorefresh} onChange={v => setAutorefresh(v.target.checked)}>Auto-refresh</Checkbox>
                    <Checkbox checked={showChart} onChange={v => setShowChart(v.target.checked)}>Chart</Checkbox>
                    <Checkbox checked={showMa} onChange={v => setShowMa(v.target.checked)}>Mov.avg.</Checkbox>
                    <Checkbox checked={useEma} onChange={v => setUseEma(v.target.checked)}>EMA</Checkbox>
                    <Checkbox checked={hideOOM} onChange={v => setHideOOM(v.target.checked)}>Hide OOM</Checkbox>
                    <Checkbox checked={showMarkers} onChange={v => setShowMarkers(v.target.checked)}>Markers</Checkbox>

                    <Select options={[
                        { label: "100", value: 100 },
                        { label: "500", value: 500 },
                        { label: "1000", value: 1000 },
                        { label: "5000", value: 5000 },
                        { label: "10000", value: 10000 },
                        { label: "All", value: 0 },
                    ]} value={lastN} onChange={setLastN} />
                </div>

                <div style={{ height: 400 }}>
                    <TrainingChart data={chartData} markers={(showMarkers ? changes : null) ?? []} lossClip={lossClip} />
                </div>
            </div>
        </div>
    );
};
